import { Injectable } from '@angular/core';
import { deleteDoc } from '@firebase/firestore';
import {
  doc,
  docData,
  runTransaction,
  Firestore,
} from '@angular/fire/firestore';
import { User } from 'firebase/auth';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { first } from 'rxjs/operators';
import { ToastFacade } from 'ngx-zcs-components';

import { AuthenticationService } from '../authentication/authentication.service';
import { ConnectionService } from '../connection/connection.service';
import { MigrationService } from '../migration/migration.service';
import { UserDocument, System, OldDevices } from '../models';
import { coreServicesLabels } from '../../core-services.service';
import { isNotNullOrUndefined, generateUUID, Semaphore } from '../utils';
import { MyCertifiedPlant } from '../telemetry';
import { GetMyPartialCertifiedPlantsQuery } from './../telemetry/queries/get-my-partial-certified-plants.query';
import { OnPlantStatusUpdateSubscription } from '../../public-api';
import { formatSegment } from '../systems/aws.systems.utils';
import { myCertifiedFragment } from './getPlant.query';
import { certifiedSubscriptionFragment } from './subscription.query';
import { processSegmentIteration, processSubscriptionVariation, removeReadOnly } from './subscription.utils';

const OLD_COLLECTION_USER = 'user';
export const COLLECTION_USER_V1 = 'user_v1';

const INFO_FROM_CLOUD = 'info_from_cloud';
const DYNAMIC_TIMEZONE = 'dynamic_timezone';
export interface TimezoneConfig {
  id: string;
};

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class StateService {
  readonly data$ = new BehaviorSubject<UserDocument | null>(null);
  readonly awsRawData$ = new BehaviorSubject<Partial<MyCertifiedPlant>[] | null>(null);
  readonly user$ = new BehaviorSubject<User | null>(null);
  private dataSubscription?: Subscription;
  private awsDataInnerSubscription?: Subscription;
  private awsDataSubscription?: Subscription;
  private readonly throttlerSave = new Semaphore();
  private readonly throttlerSaveCustomSystem = new Semaphore();

  constructor(
    private readonly afs: Firestore,
    private readonly authenticationService: AuthenticationService,
    private readonly connectionService: ConnectionService,
    private readonly migrationService: MigrationService,
    private readonly toastFacade: ToastFacade,
    private readonly onPlantStatusUpdateSubscription: OnPlantStatusUpdateSubscription,
    private readonly getMyPartialCertifiedPlantsQuery: GetMyPartialCertifiedPlantsQuery,
  ) {
    // wrap with throttle to avoid multiple calls
    this.saveCurrentSystem = this.throttlerSaveCustomSystem.wrap(this.saveCurrentSystem.bind(this));

    combineLatest([
      this.authenticationService.user$,
      this.connectionService.state$.pipe(
        isNotNullOrUndefined(),
      ),
    ]).pipe(
      untilDestroyed(this),
    ).subscribe(async ([user, connectionState]) => {
      if (!connectionState?.connected) {
        this.toastFacade.present(
          coreServicesLabels.noConnection,
          coreServicesLabels.warning,
          { severity: 'error' , mobileMode: 'single-persistent', browserMode: 'persistent' }
        );
        return;
      }
      this.user$.next(user);
      if (user && user.emailVerified) {
        await this.getResetTimezoneNeededFlag(user.uid);

        const onlyFetchData = true;
        const awsPlants = (await this.getMyPartialCertifiedPlantsQuery.execute(myCertifiedFragment, onlyFetchData).catch(() => ({
          data: {
            getMyPartialCertifiedPlants: [],
          }
        }))).data?.getMyPartialCertifiedPlants;
        this.awsRawData$.next(awsPlants);

        this.awsDataSubscription = this.onPlantStatusUpdateSubscription.execute(certifiedSubscriptionFragment).subscribe((plantsSub) => {
            this.awsDataInnerSubscription = plantsSub.subscribe((awsSystem) => {
            const operationType = awsSystem.data.onPlantStatusUpdate.operationType!;
            const awsPlants = removeReadOnly(this.awsRawData$.value)!;
            const changedAttributes = awsSystem.data.onPlantStatusUpdate.plant ?? [];
            const paths = awsSystem.data.onPlantStatusUpdate.changesPath!;

            /**
             * Iterate over all paths, and apply the changes to the awsPlants
             */
            for (let index = 0; index < paths.length; index++) {
              const changeSegments = formatSegment(paths[index]);
              let localAttribute: any = awsPlants;
              let changedAttribute: any = changedAttributes;

              /**
               * Iterate over all segments, and apply the changes to the awsPlants.
               * Usually, the last segment is the one that will be applied to the awsPlants. The other segments are used to iterate over the object.
               * But in some cases, awsPlants have missing attributes, and we need to create them (in this case ProcessSegmentIteration
               * applies the changes to the awsPlants and return shouldBreak = true).
               */
              for (let segmentIndex = 0; segmentIndex < changeSegments.length; segmentIndex++) {
                const segment = changeSegments[segmentIndex];
                const isLastSegment = segmentIndex === changeSegments.length - 1;
                if (isLastSegment) {
                  const response = processSubscriptionVariation(segment, localAttribute, changedAttribute, operationType);
                  localAttribute = response.localAttribute;
                  changedAttribute = response.changedAttribute;
                } else {
                  const response = processSegmentIteration(segment, localAttribute, changedAttribute);
                  localAttribute = response.localAttribute;
                  changedAttribute = response.changedAttribute;
                  if (response.shouldBreak) {
                    break;
                  }
                }
              }
            }

            this.awsRawData$.next(awsPlants);
          }) as Subscription;
        });

        this.dataSubscription = docData<any>(doc(this.afs, COLLECTION_USER_V1, user.uid)).subscribe(async (data) => {
          if (data === undefined || data === null || JSON.stringify(data) === JSON.stringify({})) {
            this.data$.next({ systems: [], version: 1 });
            return;
          }

          this.data$.next(data);
        });
      } else {
        this.dataSubscription?.unsubscribe();
        this.awsDataInnerSubscription?.unsubscribe();
        this.awsDataSubscription?.unsubscribe();
        this.data$.next(null);
        this.awsRawData$.next(null);
      }
    });
  }

  setSystemOnAwsRawData$(certifiedPlant: Partial<MyCertifiedPlant>) {
    const awsPlants = this.awsRawData$.value;
    if (!awsPlants) {
      return;
    }

    const index = awsPlants.findIndex((plant) => plant.plantId === certifiedPlant.plantId);
    if (index === -1) {
      return;
    }

    awsPlants[index] = certifiedPlant;
    this.awsRawData$.next(awsPlants);
  }

  async getResetTimezoneNeededFlag(uid: string) {
    const timezoneDocRef = doc(this.afs, INFO_FROM_CLOUD, DYNAMIC_TIMEZONE);
    const userDocRef = doc(this.afs, COLLECTION_USER_V1, uid);

    await runTransaction(this.afs, async (transaction) => {
      const timezoneDoc = await transaction.get(timezoneDocRef);
      if (!timezoneDoc.exists()) {
        return;
      }

      const timezoneConfig = timezoneDoc.data() as { id: string };
      if (!timezoneConfig?.id) {
        return;
      }

      const userDoc = await transaction.get(userDocRef);
      if (!userDoc.exists()) {
        return;
      }

      const userDocument = userDoc.data() as UserDocument;
      const systems = userDocument.systems ?? [];

      let isUpdateNecessary = false;

      for (const system of systems) {
        if (timezoneConfig.id !== system.configuration.latestTimezoneId) {
          isUpdateNecessary = true;
          system.configuration.timezoneEnd = null;
          system.configuration.latestTimezoneId = timezoneConfig.id;
        }
      }

      if (isUpdateNecessary) {
        userDocument.systems = systems;
        transaction.set(userDocRef, userDocument);
      }
    });
  }

  async getDebugConfig(): Promise<{ endpoint: string }> {
    const COLLECTION_REPORT = 'report';
    const DOCUMENT_DEBUG = 'debug';
    return await docData(doc(this.afs, COLLECTION_REPORT, DOCUMENT_DEBUG)).pipe(first()).toPromise() as any;
  }

  async saveCurrentSystem(system: System) {
    const uid = this.user$.value?.uid;
    if (!uid) {
      throw Error('User is required!');
    }

    const docRef = doc(this.afs, COLLECTION_USER_V1, uid);

    await runTransaction(this.afs, async (transaction) => {
      const userDoc = await transaction.get(docRef);
      let userDocument: UserDocument;

      if (!userDoc.exists()) {
        userDocument = {
          systems: [],
          version: 1,
        } as UserDocument;
      } else {
        userDocument = userDoc.data() as UserDocument;
      }

      userDocument.systems = userDocument.systems ?? [];

      /** Generate uid if undefined */
      system.uid = system.uid ?? generateUUID();

      const index = userDocument.systems.findIndex((s) => s.uid === system.uid);
      if (index !== -1) {
        userDocument.systems[index] = {
          ...system,
          source: 'firebase',
        };
      } else {
        userDocument.systems.push({
          ...system,
          source: 'firebase',
        });
      }

      transaction.set(docRef, userDocument);
    });
  }

  async removeCurrentSystem(systemUid: string) {
    const uid = this.user$.value?.uid;
    if (!uid) {
      throw Error('User is required!');
    }

    const docRef = doc(this.afs, COLLECTION_USER_V1, uid);

    await runTransaction(this.afs, async (transaction) => {
      const userDoc = await transaction.get(docRef);

      if (!userDoc.exists()) {
        throw Error('UserDocument does not exist!');
      }

      const userDocument = userDoc.data() as UserDocument;

      userDocument.systems = userDocument.systems.filter((s: System) => s.uid !== systemUid);

      transaction.update(docRef, { systems: userDocument.systems });
    });
  }

  async saveOperatorCmds(cmds: string[]) {
    const user = await this.authenticationService.user$.pipe(first()).toPromise();
    if (!user) {
      throw Error('User is required!');
    }

    const docRef = doc(this.afs, COLLECTION_USER_V1, user.uid);

    await runTransaction(this.afs, async (transaction) => {
      const userDoc = await transaction.get(docRef);
      let userDocument: UserDocument;

      if (!userDoc.exists()) {
        throw Error('UserDocument does not exist!');
      } else {
        userDocument = userDoc.data() as UserDocument;
      }

      userDocument.operator = {
        ...userDocument.operator,
        cmds,
      };

      transaction.set(docRef, userDocument);
    });
  }

  async savePortalOperatorCmds(cmdObj: { key: string; value: string }[]) {
    const user = await this.authenticationService.user$.pipe(first()).toPromise();
    if (!user) {
      throw Error('User is required!');
    }

    const docRef = doc(this.afs, COLLECTION_USER_V1, user.uid);

    await runTransaction(this.afs, async (transaction) => {
      const userDoc = await transaction.get(docRef);
      let userDocument: UserDocument;

      if (!userDoc.exists()) {
        throw Error('UserDocument does not exist!');
      } else {
        userDocument = userDoc.data() as UserDocument;
      }

      userDocument.operator = {
        ...userDocument.operator,
        cmdObj,
      };

      transaction.set(docRef, userDocument);
    });
  }

  async getCurrentSystemByFirebase(uid: string): Promise<System | undefined> {
    const userId = this.user$.value?.uid!;
    const userDocument = await docData<any>(doc(this.afs, COLLECTION_USER_V1, userId)).pipe(first()).toPromise();
    if (!userDocument) {
      return;
    }
    const currentSystem = userDocument.systems.find((s: System) => s.uid === uid);
    return currentSystem ? {
      ...currentSystem,
      source: 'firebase',
    } : currentSystem;
  }

  getCurrentSystem(uid: string): System | undefined {
    const userDocument = this.data$.value;
    if (!userDocument) {
      return;
    }
    const currentSystem = userDocument.systems.find((s) => s.uid === uid);
    return currentSystem ?{
      ...currentSystem,
      source: 'firebase',
    } : currentSystem;
  }

  async recoverInheritedSystems(): Promise<System[] | undefined> {
    const user = await this.user$.pipe(
      isNotNullOrUndefined(),
      first(),
    ).toPromise();

    if (!user) {
      return;
    }

    const data = await docData<OldDevices>(doc(this.afs, OLD_COLLECTION_USER, user.uid))
      .pipe(first()).toPromise();

    if (data === undefined || data === null || JSON.stringify(data) === JSON.stringify({})) {
      return [];
    }

    /** Remove plants without connext device */
    return this.migrationService.adapter(data).filter((system) => {
      const systemWithConnextID = !!system.configuration.connextId;
      const systemWithConnextDevice = system.devices.find((device) => device.type === 'connext');
      return (systemWithConnextID && !systemWithConnextDevice) ? false : true;
    });
  }

  async removeUserDataByRequest(removeAccount: boolean) {
    const user = await this.authenticationService.user$.pipe(first()).toPromise();
    if (!user) {
      throw Error('User is required!');
    }
    const referenceUserV1 = doc(this.afs, COLLECTION_USER_V1, user.uid);
    const referenceUserOld = doc(this.afs, OLD_COLLECTION_USER, user.uid);

    await Promise.race([
      deleteDoc(referenceUserOld),
      deleteDoc(referenceUserV1),
    ]);

    if (removeAccount) {
      await this.authenticationService.deleteUserAccount();
      await this.authenticationService.signOut(true);
    }
  }
}
